/* eslint-disable no-shadow */
import { END, buffers, eventChannel } from "redux-saga";
import {
    call, cancel, cancelled, delay, flush, fork, race, take, takeEvery
} from "redux-saga/effects";
import noop from "lodash.noop";
import { ONE_SECOND } from "Globals/constants/constants";

import {
    ARCHIVE_REQUESTED,
    CREATE_REQUESTED, 
    LOAD_REQUESTED,
    SUBSCRIBE,
    UNSUBSCRIBE,
    UPDATE_REQUESTED,
    defaultScopeId,
    failLoadWorkflow,
    failWorkflowArchive,
    failWorkflowCreate,
    failWorkflowUpdate,
    requestLoadWorkflow,
    succeedWorkflowArchive,
    succeedWorkflowCreate,
    succeedWorkflowUpdate,
    successLoadWorkflow,
    updateManyWorkflows
} from "./workflowEditor.duck";

import {
    archiveWorkflow,
    createWorkflow,
    loadWorkflow,
    subscribeWorkflows,
    updateWorkflow
} from "./workflowEditor.api";

import { getId, injectBoundActionCreatorsFactory } from "Utils/sagaUtils";

export const withBoundActions = injectBoundActionCreatorsFactory(
    {
        failLoadWorkflow,
        failWorkflowArchive,
        failWorkflowCreate,
        failWorkflowUpdate,
        requestLoadWorkflow,
        succeedWorkflowArchive,
        succeedWorkflowCreate,
        succeedWorkflowUpdate,
        successLoadWorkflow,
        updateManyWorkflows
    },
    getId,
    defaultScopeId
);

const transformRawWorkflowForEditor = ({ id, stages }) => {
    const nodes = stages.map(({ id: stageId, ...stage }, idx) => ({
        data: {
            values: {
                ...stage,
            }
        },
        id: `${stageId}`,
        // eslint-disable-next-line no-magic-numbers
        position: { x: 600*idx || 50, y: 80 }, // magic numbers here to lay out editor nodes
        type: "workflowNode",
    }));

    
    const edges = stages.reduce((allEdges, stage) => {
        const nextEdges = stage.nextStages.map(next => ({
            arrowHeadType: "arrowclosed",
            id: `${stage.id}_${next}`,
            source: `${stage.id}`,
            style: {
                strokeWidth: "2",
            },
            target: `${next}`,
            type: "default"
        }));
        return [ ...allEdges, ...nextEdges ];
    },[]);

    return {
        id,
        elements: [ ...nodes, ...edges ]
    };
};

const transformEditWorkflowForAPI = ({ elements }) => {
    const stages = elements
        .filter(({ type }) => type === "workflowNode")
        .map(
            ({ data, id }) =>
                ({ ...data.values, id } || {
                    id,
                    nextStages: [],
                })
        );
    return stages.map(stage => {
        const updatedStages = elements.filter(({ source }) => source === stage.id).map(({ target }) => target);
        return { ...stage, nextStages: updatedStages };
    });
};

export function* createWorkflowSaga({
    data,
    actions: { succeedWorkflowCreate, failWorkflowCreate },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield call(createWorkflow, data);
        const reactFlowWorkflow = transformRawWorkflowForEditor(result);
        yield succeedWorkflowCreate(reactFlowWorkflow);
        yield call(resolve);
    } catch (e) {
        yield failWorkflowCreate(e);
        yield call(reject, e);
    }
}

export function* updateWorkflowSaga({
    data: { workflowId, ...workflow },
    actions: { succeedWorkflowUpdate, failWorkflowUpdate },
    resolve = noop,
    reject = noop,
}) {
    try {
        const updatedWorkflow = transformEditWorkflowForAPI(workflow);
        const result = yield call(updateWorkflow, { id: workflowId, stages: updatedWorkflow });
        const reactFlowWorkflow = transformRawWorkflowForEditor(result);
        yield succeedWorkflowUpdate(reactFlowWorkflow);
        yield call(resolve);
    } catch (e) {
        yield failWorkflowUpdate(e);
        yield call(reject, e);
    }
}

export function* archiveWorkflowSaga({
    data, actions: { succeedWorkflowArchive, failWorkflowArchive }, resolve = noop, reject = noop 
}){
    try {
        const result = yield call(archiveWorkflow, data);
        const reactFlowWorkflow = transformRawWorkflowForEditor(result);
        yield succeedWorkflowArchive(reactFlowWorkflow);
        yield call(resolve);
    } catch (e){
        yield failWorkflowArchive(e);
        yield call(reject, e);
    }
}

export function* loadWorkflowsSaga({ data, actions: { successLoadWorkflow, failLoadWorkflow }, resolve = noop, reject = noop }){
    try {
        const result  = yield call(loadWorkflow, data);
        const reactFlowWorkflow = transformRawWorkflowForEditor(result);
        yield successLoadWorkflow(reactFlowWorkflow);
        yield call(resolve);
    } catch (e){
        yield failLoadWorkflow(e);
        yield call(reject, e);
    }
}

export const workflowSubscription = subject => {
    return eventChannel(emitter => {
        try {
            return subscribeWorkflows(subject, emitter);
        } catch (e){
            emitter(END);
        }
    },
    buffers.expanding(1) // buffer one invocation holding the most recent event
    );
};

export function* subscribeToWorkflows({ subject, actions: { updateManyWorkflows } }) {
    const chan = yield call(workflowSubscription, subject);
    try {
        while (true) {
            const workflows = yield flush(chan);
            if(workflows.length) {
                yield updateManyWorkflows({ workflows: workflows.flat().map(transformRawWorkflowForEditor) });
            }
            yield delay(ONE_SECOND);
        }
    }
    finally {
        if (yield cancelled()) {
            chan.close();
        }
    }
  
}

export function* subscribeWorkflowsSaga(){
    const subscriptions = {};
    const tasks = {};
    while ( true ) {
        const { subscribe, unsubscribe } = yield race({
            subscribe: take(SUBSCRIBE),
            unsubscribe: take(UNSUBSCRIBE)
        });
        const { data: { workflowId } } = subscribe || unsubscribe;
        const ids = [workflowId].filter(i => i);
        if(subscribe) {
            for(const subId of ids) {
                subscriptions[subId] = subscriptions[subId] || 0;
                subscriptions[subId]++;
                if(subscriptions[subId] === 1) {
                    tasks[subId] = yield fork(withBoundActions(subscribeToWorkflows), { ...(subscribe || unsubscribe), subject: subId });
                }
            }
        }
        if(unsubscribe) {
            for(const subId of ids) {
                subscriptions[subId]--;
                if(subscriptions[subId] === 0) {
                    yield cancel(tasks[subId]);
                }
            
            }
        }
    }
}


export default () => function* rootSaga() {
    yield takeEvery(CREATE_REQUESTED, withBoundActions(createWorkflowSaga));
    yield takeEvery(UPDATE_REQUESTED, withBoundActions(updateWorkflowSaga));
    yield takeEvery(ARCHIVE_REQUESTED, withBoundActions(archiveWorkflowSaga));
    yield takeEvery(LOAD_REQUESTED, withBoundActions(loadWorkflowsSaga));
    yield fork(subscribeWorkflowsSaga);
};
